import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Space } from '@components/atoms/Space';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Typography } from '@components/atoms/Typography';
import { Select, SelectType } from '@components/atoms/Select';
import { Button, ButtonMode } from '@components/atoms/Button';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { useGetAllProductsCategories } from '@services/useGetAllProductsCategories';
import { useCreateProduct } from '@services/useCreateProduct';
import { useEditProduct } from '@services/useEditProduct';
import { useGetMyAccountProductDetail } from '@services/useGetMyAccountProductDetail';
import { useGetAllMyAccountStores } from '@services/useGetAllMyAccountStores';
import { InputErrors, Translations } from '@types';
import { convertFilesToBase64 } from '@utils/image';
import styles from './ProductForm.module.css';
import { TranslationTabs } from '../TranslationTabs';
import { updateTranslation } from '@utils/translation';

interface ProductFormErrors {
    general: string;
    name: InputErrors;
    category: InputErrors;
    store: InputErrors;
    weight: InputErrors;
    state: InputErrors;
    hasVariants: InputErrors;
    image: InputErrors;
}

interface ProductFormProps {
    edit: boolean;
}

export const ProductForm: FC<ProductFormProps> = ({ edit }) => {
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const {
        allProductsCategories,
        isLoadingAllProductsCategories,
        login,
        myAccountProductDetail,
        isLoadingMyAccountProductDetail,
        createdProduct,
        allMyAccountStores,
        isLoadingAllMyAccountStores,
        allLanguages,
        allSettings,
    } = useData();
    const { request: requestAllProductsCategories } = useGetAllProductsCategories();
    const {
        request: requestCreateProduct,
        errorMessage: errorMessageCreate,
        validationErrors: validationErrorsCreate,
        success: successMessageCreate,
        lastResponseTime: lastCreate,
    } = useCreateProduct();
    const {
        request: requestEditProduct,
        errorMessage: errorMessageEdit,
        validationErrors: validationErrorsEdit,
        success: successMessageEdit,
        lastResponseTime: lastEdit,
    } = useEditProduct();
    const { request: requestMyAccountProductDetail } = useGetMyAccountProductDetail();
    const { request: requestAllMyAccountStores } = useGetAllMyAccountStores();
    const { t } = useTranslation();

    const automaticTranslations = allSettings?.data?.module_automatic_translations?.active;
    const languagesToTranslate = allLanguages?.data?.filter((item) => item.default !== 1) || [];

    const translations = {
        nameLabel: t('manage-products-form.products.name.label'),
        categoryLabel: t('manage-products-form.products.category.label'),
        storeLabel: t('manage-products-form.products.store.label'),
        weightLabel: t('manage-products-form.products.weight.label'),
        stateLabel: t('manage-products-form.products.state.label'),
        imageLabel: t('manage-products-form.products.image.label'),
        descriptionLabel: t('manage-products-form.products.description.label'),
        aboutLabel: t('manage-products-form.products.about.label'),
        hasVariantsLabel: t('manage-products-form.products.has-variants.label'),
        hasVariants: t('manage-products-form.products.has-variants.yes'),
        hasNoVariants: t('manage-products-form.products.has-variants.no'),
        nameRequiredError: t('manage-products-form.products.name.required-error'),
        categoryRequiredError: t('manage-products-form.products.category.required-error'),
        storeRequiredError: t('manage-products-form.products.store.required-error'),
        weightRequiredError: t('manage-products-form.products.weight.required-error'),
        stateRequiredError: t('manage-products-form.products.state.required-error'),
        hasVariantsRequiredError: t('manage-products-form.products.has-variants.required-error'),
        imageRequiredError: t('manage-products-form.products.image.required-error'),
        submitButton: t('manage-products-form.submit.button'),
        default: t('select-default-option'),
        productStateActive: t('product-state.active'),
        productStateInactive: t('product-state.inactive'),
        productStateOutOfStock: t('product-state.out-of-stock'),
    };

    // call services, if we don't have the information yet
    useEffect(() => {
        !allProductsCategories && !isLoadingAllProductsCategories && requestAllProductsCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allProductsCategories]);

    useEffect(() => {
        !allMyAccountStores && !isLoadingAllMyAccountStores && requestAllMyAccountStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMyAccountStores]);

    const formRef = useRef<HTMLFormElement>(null);
    const [name, setName] = useState<string>('');
    const [category, setCategory] = useState<string | null>(null);
    const [store, setStore] = useState<string | null>(null);
    const [state, setState] = useState<string | null>(null);
    const [weight, setWeight] = useState<number>(0);
    const [image, setImage] = useState<string | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [description, setDescription] = useState<string>('');
    const [about, setAbout] = useState<string>('');
    const [hasVariants, setHasVariants] = useState<string>('');
    const [nameTranslations, setNameTranslations] = useState<Translations>([]);
    const [descriptionTranslations, setDescriptionTranslations] = useState<Translations>([]);
    const [aboutTranslations, setAboutTranslations] = useState<Translations>([]);

    // force the image input to reset
    const [imagesInputKey, setImagesInputKey] = useState(Date.now());

    const stores = (allMyAccountStores?.data || [])
        // filter by stores that may have products
        .filter((item) => item.is_product_store && item.status !== 'pending')
        .map((item) => {
            return { value: item.uuid || '', text: item.name || '' };
        });

    // initially, update fields
    useEffect(() => {
        if (edit) {
            setName(myAccountProductDetail?.data?.product?.name || '');
            setCategory(myAccountProductDetail?.data?.product?.category || '');
            setStore(myAccountProductDetail?.data?.product?.store || '');
            setState(myAccountProductDetail?.data?.product?.status || '');
            setWeight(myAccountProductDetail?.data?.product?.weight || 0);
            setImagePreview(myAccountProductDetail?.data?.product?.image?.src || null);
            setDescription(myAccountProductDetail?.data?.product?.description || '');
            setAbout(myAccountProductDetail?.data?.product?.about || '');
            setHasVariants(myAccountProductDetail?.data?.product?.has_variants || '');
            setNameTranslations(
                myAccountProductDetail?.data?.translations
                    ?.filter((translation) => translation.name === 'name')
                    .map((translation) => {
                        return { languageCode: translation.language_code || '', translation: translation.text || '' };
                    }) || []
            );
            setDescriptionTranslations(
                myAccountProductDetail?.data?.translations
                    ?.filter((translation) => translation.name === 'description')
                    .map((translation) => {
                        return { languageCode: translation.language_code || '', translation: translation.text || '' };
                    }) || []
            );
            setAboutTranslations(
                myAccountProductDetail?.data?.translations
                    ?.filter((translation) => translation.name === 'about')
                    .map((translation) => {
                        return { languageCode: translation.language_code || '', translation: translation.text || '' };
                    }) || []
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myAccountProductDetail]);

    // errors to be displayed in the form
    const [errors, setErrors] = useState<ProductFormErrors>({
        general: '',
        name: [],
        category: [],
        store: [],
        weight: [],
        state: [],
        hasVariants: [],
        image: [],
    });

    const handleOnClickSaveProduct = () => {
        const errors = validateForm();

        if (
            errors.name.length === 0 &&
            errors.category.length === 0 &&
            errors.store.length === 0 &&
            errors.weight.length === 0 &&
            errors.state.length === 0 &&
            errors.hasVariants.length === 0 &&
            errors.image.length === 0
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => ProductFormErrors = () => {
        const newErrors: ProductFormErrors = {
            general: '',
            name: [],
            category: [],
            store: [],
            state: [],
            weight: [],
            hasVariants: [],
            image: [],
        };

        !name.trim() && newErrors.name.push(translations.nameRequiredError);
        !category && newErrors.category.push(translations.categoryRequiredError);
        !store && newErrors.store.push(translations.storeRequiredError);
        !weight && newErrors.weight.push(translations.weightRequiredError);
        !state && newErrors.state.push(translations.stateRequiredError);
        !hasVariants.trim() && newErrors.hasVariants.push(translations.hasVariantsRequiredError);
        // image is required only in create form
        if (!edit) {
            !image && newErrors.image.push(translations.imageRequiredError);
        }

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = async () => {
        const imageInput = document.getElementById('image-input') as HTMLInputElement;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const base64Images: any = imageInput.files ? await convertFilesToBase64(imageInput.files) : null;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const inputTranslations: any = {};
        nameTranslations.forEach((item) => (inputTranslations[`name_${item.languageCode}`] = item.translation));
        descriptionTranslations.forEach((item) => (inputTranslations[`description_${item.languageCode}`] = item.translation));
        aboutTranslations.forEach((item) => (inputTranslations[`about_${item.languageCode}`] = item.translation));

        const data = {
            name: name,
            category_uuid: category || '',
            store_uuid: store || '',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            status: (state || '') as any,
            image: base64Images[0] || undefined,
            weight: weight,
            description: description,
            about: about,
            has_variants: hasVariants ? (Number(hasVariants) as 0 | 1) : undefined,
            ...inputTranslations,
        };

        if (edit === true) {
            requestEditProduct({ uuid: myAccountProductDetail?.data?.uuid || '' }, data);
        } else {
            requestCreateProduct(data);
        }
    };

    // if create service returned successfully, dispatch notification and go to product's edit page
    // if not, show errors
    useEffect(() => {
        if (lastCreate) {
            if (errorMessageCreate || validationErrorsCreate) {
                setErrors({
                    general: errorMessageCreate as string,
                    // match form request errors with frontend errors object
                    name: validationErrorsCreate?.name || [],
                    category: validationErrorsCreate?.category_id || [],
                    store: validationErrorsCreate?.store_id || [],
                    weight: validationErrorsCreate?.weight || [],
                    state: validationErrorsCreate?.status || [],
                    hasVariants: validationErrorsCreate?.has_variants || [],
                    image: validationErrorsCreate?.image || [],
                });
            } else if (login && createdProduct !== null) {
                showDefaultNotification({ type: 'success', message: successMessageCreate || '' });
                navigate(`/${SITE_URLS.myProductsEdit.replace(':uuid', `${createdProduct?.data.uuid}`)}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCreate, createdProduct]);

    // if edit service returned successfully, dispatch notification and update
    // if not, show error
    useEffect(() => {
        if (lastEdit) {
            if (errorMessageEdit || validationErrorsEdit) {
                setErrors({
                    general: errorMessageEdit as string,
                    // match form request errors with frontend errors object
                    name: validationErrorsEdit?.name || [],
                    category: validationErrorsEdit?.category_id || [],
                    store: validationErrorsEdit?.store_id || [],
                    weight: validationErrorsEdit?.weight || [],
                    state: validationErrorsEdit?.status || [],
                    hasVariants: validationErrorsEdit?.has_variants || [],
                    image: validationErrorsEdit?.image || [],
                });
            } else if (login) {
                showDefaultNotification({ type: 'success', message: successMessageEdit || '' });
                // update form fields
                setImagesInputKey(Date.now());
                setImage(null);
                requestMyAccountProductDetail({ uuid: myAccountProductDetail?.data?.uuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastEdit]);

    return (
        <Form formRef={formRef} onSubmit={() => submitForm()}>
            <>
                {errors.general && (
                    <>
                        <ErrorMessages messages={[errors.general]} />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </>
                )}
            </>

            <div className="row">
                {/* name */}
                <div className="col-12">
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.labeledInput}
                        label={`${translations.nameLabel}*`}
                        value={name}
                        onChange={(value) => setName(value)}
                        disabled={isLoadingMyAccountProductDetail}
                        state={errors.name.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.name}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* category */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.categoryLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={
                                isLoadingMyAccountProductDetail ||
                                allProductsCategories === null ||
                                isLoadingAllProductsCategories
                            }
                            options={[
                                { groupName: '', groupOptions: [{ value: '', text: translations.default }] },
                                ...(allProductsCategories?.data || []).map((group) => {
                                    return {
                                        groupName: group.name || '',
                                        groupOptions:
                                            group.children?.map((item) => {
                                                return { value: item.uuid || '', text: item.name || '' };
                                            }) || [],
                                    };
                                }),
                            ]}
                            value={category || undefined}
                            onChange={(value) => setCategory(value)}
                        />
                        {errors.category.length > 0 && <ErrorMessages messages={errors.category} />}
                    </div>

                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* store */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.storeLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={
                                isLoadingMyAccountProductDetail || allMyAccountStores === null || isLoadingAllMyAccountStores
                            }
                            options={[{ value: '', text: translations.default }, ...stores]}
                            value={store || undefined}
                            onChange={(value) => setStore(value)}
                        />
                        {errors.store.length > 0 && <ErrorMessages messages={errors.store} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* state */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.stateLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoadingMyAccountProductDetail}
                            options={[
                                { value: '', text: translations.default },
                                { value: 'active', text: translations.productStateActive },
                                { value: 'inactive', text: translations.productStateInactive },
                                { value: 'out_of_stock', text: translations.productStateOutOfStock },
                            ]}
                            value={state || undefined}
                            onChange={(value) => setState(value)}
                        />
                        {errors.state.length > 0 && <ErrorMessages messages={errors.state} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* has variant */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.hasVariantsLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoadingMyAccountProductDetail}
                            options={[
                                { value: '', text: translations.default },
                                { value: '1', text: translations.hasVariants },
                                { value: '0', text: translations.hasNoVariants },
                            ]}
                            value={`${hasVariants}` || undefined}
                            onChange={(value) => setHasVariants(value)}
                        />
                        {errors.hasVariants.length > 0 && <ErrorMessages messages={errors.hasVariants} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.number}
                        variant={InputVariants.labeledInput}
                        label={`${translations.weightLabel}*`}
                        value={`${weight}`}
                        onChange={(value) => setWeight(value)}
                        disabled={isLoadingMyAccountProductDetail}
                        state={errors.weight.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.weight}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* description */}
                <div className="col-12">
                    <Input
                        type={InputTypes.textarea}
                        variant={InputVariants.labeledInput}
                        label={translations.descriptionLabel}
                        value={description}
                        disabled={isLoadingMyAccountProductDetail}
                        onChange={(value) => setDescription(value)}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                <div className="col-12">
                    <Input
                        type={InputTypes.textarea}
                        variant={InputVariants.labeledInput}
                        label={translations.aboutLabel}
                        value={about}
                        disabled={isLoadingMyAccountProductDetail}
                        onChange={(value) => setAbout(value)}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* image */}
                <div className="col-12">
                    <Input
                        key={imagesInputKey}
                        id="image-input"
                        type={InputTypes.file}
                        label={`${translations.imageLabel}*`}
                        variant={InputVariants.labeledInput}
                        value={image || ''}
                        disabled={isLoadingMyAccountProductDetail}
                        onChange={(value) => setImage(value)}
                        state={errors.image.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.image}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    {!isLoadingMyAccountProductDetail && imagePreview && (
                        <img
                            src={imagePreview}
                            className={styles['image-preview']}
                            alt={myAccountProductDetail?.data?.product?.name}
                        />
                    )}
                </div>
            </div>
            {!automaticTranslations ? (
                <TranslationTabs
                    contentByLanguage={languagesToTranslate.map((language) => {
                        return {
                            language: language.language_code || '',
                            content: (
                                <>
                                    <div className="row">
                                        <div className="col-md-6 col-6">
                                            <div className="d-flex flex-column gap-3">
                                                <Input
                                                    type={InputTypes.text}
                                                    label={translations.nameLabel}
                                                    variant={InputVariants.labeledInput}
                                                    value={
                                                        nameTranslations.find(
                                                            (item) => item.languageCode === language.language_code
                                                        )?.translation || ''
                                                    }
                                                    onChange={(value) =>
                                                        setNameTranslations((previous) =>
                                                            updateTranslation(previous, language.language_code || '', value)
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-6">
                                            <div className="d-flex flex-column gap-3">
                                                <Input
                                                    type={InputTypes.text}
                                                    label={translations.descriptionLabel}
                                                    variant={InputVariants.labeledInput}
                                                    value={
                                                        descriptionTranslations.find(
                                                            (item) => item.languageCode === language.language_code
                                                        )?.translation || ''
                                                    }
                                                    onChange={(value) =>
                                                        setDescriptionTranslations((previous) =>
                                                            updateTranslation(previous, language.language_code || '', value)
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                    <div className="row">
                                        <div className="col-md-6 col-12">
                                            <div className="d-flex flex-column gap-3">
                                                <Input
                                                    type={InputTypes.text}
                                                    label={translations.aboutLabel}
                                                    variant={InputVariants.labeledInput}
                                                    value={
                                                        aboutTranslations.find(
                                                            (item) => item.languageCode === language.language_code
                                                        )?.translation || ''
                                                    }
                                                    onChange={(value) =>
                                                        setAboutTranslations((previous) =>
                                                            updateTranslation(previous, language.language_code || '', value)
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ),
                        };
                    })}
                />
            ) : (
                <></>
            )}

            {/* submit */}
            <div className="col-12 text-end">
                <Button
                    icon="fa-solid fa-floppy-disk"
                    label={translations.submitButton}
                    mode={ButtonMode.primary}
                    disabled={isLoadingMyAccountProductDetail}
                    onClick={() => handleOnClickSaveProduct()}
                />
            </div>
        </Form>
    );
};
